import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState, useRef, useEffect } from "react";
import {
  RefreshCw,
  Send,
  Loader2,
  User,
  X,
  FileIcon,
  Download,
  Image,
  FileText,
  PaperclipIcon,
  Check,
  Clock,
  Camera,
  Truck,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { addMessageToProcessStepSale } from "@/Api/sales";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/auth";
import { SALE_STEPS, SALE_TYPE_ID } from "@/utils/constants";
import { If } from "../If";
import { CameraModal } from "@/components/CameraModal";

interface StepDialogProps {
  isOpen: boolean;
  onClose: () => void;
  stepId: number;
  stepName: string;
  currentStatus: {
    id: number;
    name: string;
  };
  onUpdateStatus: (statusId: number, trackingCode?: string) => Promise<void>;
  chats: Array<{
    id: number;
    message: string;
    created_at: string;
    author: {
      name: string;
    };
    text?: string;
    author_id?: number;
    file?: string;
  }>;
  onChatAdded: () => Promise<void>;
  saleStepId: number;
  saleTypeId?: number;
  trackingCode?: string;
}

export function StepDialog({
  isOpen,
  onClose,
  stepId,
  stepName,
  currentStatus,
  onUpdateStatus,
  chats,
  onChatAdded,
  saleStepId,
  saleTypeId,
  trackingCode: initialTrackingCode,
}: StepDialogProps) {
  const [loading, setLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>(
    currentStatus.id.toString()
  );
  const [message, setMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [statusChanged, setStatusChanged] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [trackingCode, setTrackingCode] = useState(initialTrackingCode || "");
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const user = useAuth();
  const userMyId = user?.user?.id;

  const isSedexSeparation = saleTypeId === SALE_TYPE_ID.SEDEX && saleStepId === SALE_STEPS.IN_SEPARATION;

  useEffect(() => {
    setSelectedStatus(currentStatus.id.toString());
    setStatusChanged(false);
    setTrackingCode(initialTrackingCode || "");
  }, [currentStatus, initialTrackingCode]);

  const scrollToBottom = () => {
    if (scrollAreaRef.current) {
      const scrollElement = scrollAreaRef.current;
      setTimeout(() => {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }, 100);
    }
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [isOpen, chats]);

  useEffect(() => {
    if (chats && chats.length > 0) {
      scrollToBottom();
    }
  }, [chats]);

  const handleStatusChange = (newStatus: string) => {
    setSelectedStatus(newStatus);
    setStatusChanged(true);
  };

  const handleApplyStatus = async () => {
    if (!statusChanged) return;

    const newStatus = parseInt(selectedStatus);

    if (newStatus === 3 && !chats.some(chat => chat.file)) {
      toast.error("É obrigatório anexar pelo menos um arquivo para finalizar esta etapa. Envie uma mensagem com anexo primeiro.");
      return;
    }

    if (newStatus === 3 && isSedexSeparation && !trackingCode.trim()) {
      toast.error("É obrigatório informar o código de rastreio para finalizar a etapa de separação de vendas SEDEX.");
      return;
    }

    setLoading(true);
    try {
      await onUpdateStatus(newStatus, isSedexSeparation ? trackingCode.trim() : undefined);
    } catch (error) {
      toast.error("Erro ao atualizar status");
      setSelectedStatus(currentStatus.id.toString());
      setStatusChanged(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() && selectedFiles.length === 0) return;

    setSendingMessage(true);
    try {
      if (selectedFiles.length > 0) {
        for (let i = 0; i < selectedFiles.length; i++) {
          const data = {
            process_step_sale_id: stepId,
            text: i === 0 ? (message.trim() || "Arquivo enviado") : "Arquivo enviado",
            file: selectedFiles[i],
          };
          await addMessageToProcessStepSale(data);
        }
      } else {
        const data = {
          process_step_sale_id: stepId,
          text: message.trim(),
          file: undefined,
        };
        await addMessageToProcessStepSale(data);
      }

      setMessage("");
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      await onChatAdded();

      setTimeout(scrollToBottom, 200);

      toast.success("Mensagem enviada com sucesso!");
    } catch (error) {
      toast.error("Erro ao enviar mensagem");
    } finally {
      setSendingMessage(false);
    }
  };

  const getFileIcon = (fileUrl: string) => {
    const fileLinkExtension = fileUrl.split(".")?.pop()?.toLowerCase();
    const extension = fileLinkExtension?.split("?")?.[0];
    switch (extension) {
      case "jpg":
      case "jpeg":
      case "png":
        return <Image className="h-4 w-4" />;
      case "pdf":
        return <FileText className="h-4 w-4" />;
      default:
        return <FileIcon className="h-4 w-4" />;
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.length) return;

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "image/png",
      "image/jpeg",
      "image/jpg",
      "video/mp4",
      "video/webm",
      "video/ogg",
      "video/avi",
      "video/mov",
      "video/quicktime",
    ];

    const validFiles: File[] = [];
    const files = Array.from(event.target.files);

    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`O arquivo "${file.name}" deve ter no máximo 5MB`);
        continue;
      }

      if (!allowedTypes.includes(file.type)) {
        toast.error(`Tipo de arquivo não permitido: "${file.name}"`);
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles]);
      toast.success(`${validFiles.length} arquivo(s) selecionado(s) com sucesso!`);
    }

    event.target.value = "";
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCameraCapture = (file: File) => {
    setSelectedFiles((prev) => [...prev, file]);
    toast.success("Foto capturada com sucesso!");
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStepStatusIcon = (statusId: number, stepId: number) => {
    if ((stepId === 3 || stepId === 7) && statusId === 3) {
      return <X className="w-4 h-4 text-red-600" />;
    }

    switch (statusId) {
      case 1:
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case 2:
        return <Loader2 className="w-4 h-4 text-blue-600" />;
      case 3:
        return <Check className="w-4 h-4 text-green-600" />;
      case 4:
        return <X className="w-4 h-4 text-red-600" />;
      default:
        return null;
    }
  };

  const getStepStatusName = (
    status: string,
    stepId: number,
    statusId: number
  ) => {
    if ((stepId === 3 || stepId === 7) && statusId === 3) {
      return "Recusado";
    }
    return status;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
        onClick={() => !showCamera && onClose()}
      />

      {/* Modal Content */}
      <div className="fixed left-[50%] top-[50%] z-50 w-[95vw] max-w-md sm:max-w-lg md:max-w-xl translate-x-[-50%] translate-y-[-50%] border border-border bg-zinc-50 dark:bg-zinc-950 dark:text-zinc-50 p-3 sm:p-6 shadow-lg rounded-lg">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>

        <div className="flex flex-col h-[70vh] max-h-[80vh]">
          {/* Header */}
          <div className="border-b pb-4 flex-shrink-0">
            <div className="flex items-center gap-2 text-lg font-semibold leading-none tracking-tight">
                <span className="font-semibold">{stepName}</span>
                <div className="flex-1" />
                <div className="flex items-center gap-3">
                  {(saleStepId === 3 || saleStepId === 7) &&
                  currentStatus.id === 3 ? (
                    <div className="flex items-center gap-2 bg-red-100 text-red-700 px-3 py-1 rounded-md">
                      {getStepStatusIcon(currentStatus.id, saleStepId)}
                      <span className="text-sm font-medium">
                        {getStepStatusName(
                          currentStatus.name,
                          saleStepId,
                          currentStatus.id
                        )}
                      </span>
                    </div>
                  ) : saleStepId === 2 && currentStatus.id === 3 ? (
                    <div className="flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-md">
                      {getStepStatusIcon(currentStatus.id, saleStepId)}
                      <span className="text-sm font-medium">
                        Pagamento Confirmado
                      </span>
                    </div>
                  ) : currentStatus.id === 3 ? (
                    <div className="flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-md">
                      {getStepStatusIcon(currentStatus.id, saleStepId)}
                      <span className="text-sm font-medium">Finalizado</span>
                    </div>
                  ) : (
                    <>
                      <If condition={saleStepId != SALE_STEPS.WAITING_PAYMENT}>
                        <div className="flex-1">
                          <Select
                            value={selectedStatus}
                            onValueChange={handleStatusChange}
                            disabled={
                              loading ||
                              currentStatus.id === 3 ||
                              saleStepId === 3 ||
                              saleStepId === 7
                            }
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue placeholder="Selecione o status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">Não iniciado</SelectItem>
                              <SelectItem value="2">Em andamento</SelectItem>
                              <SelectItem value="3">Finalizado</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={handleApplyStatus}
                          disabled={
                            !statusChanged ||
                            loading ||
                            currentStatus.id === 3 ||
                            saleStepId === 3 ||
                            saleStepId === 7
                          }
                          className="h-8 px-3"
                        >
                          {loading ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            "Aplicar"
                          )}
                        </Button>
                        {selectedStatus === "3" && !chats.some(chat => chat.file) && (
                          <div className="text-xs text-yellow-600 dark:text-yellow-400 ml-2">
                            Anexo obrigatório
                          </div>
                        )}
                      </If>
                    </>
                  )}
                </div>
              </div>
            </div>

            {isSedexSeparation && currentStatus.id !== 3 && (
              <div className="border-b pb-4 pt-2 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <Truck className="w-4 h-4 text-muted-foreground" />
                  <label htmlFor="tracking-code" className="text-sm font-medium">
                    Código de Rastreio (SEDEX)
                  </label>
                </div>
                <div className="mt-2 flex gap-2">
                  <Input
                    id="tracking-code"
                    value={trackingCode}
                    onChange={(e) => setTrackingCode(e.target.value.toUpperCase())}
                    placeholder="Ex: AA123456789BR"
                    className="flex-1"
                    maxLength={50}
                  />
                </div>
                {selectedStatus === "3" && !trackingCode.trim() && (
                  <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                    Código de rastreio obrigatório para finalizar
                  </p>
                )}
                {initialTrackingCode && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Código atual: {initialTrackingCode}
                  </p>
                )}
              </div>
            )}

            <div
              className="flex-1 overflow-y-auto p-4 space-y-4"
              ref={scrollAreaRef}
            >
              {chats.length > 0 ? (
                chats.map((chat) => (
                  <div
                    key={chat.id}
                    className={cn(
                      "flex gap-3 items-start",
                      chat.author_id === userMyId
                        ? "flex-row-reverse"
                        : "flex-row"
                    )}
                  >
                    <div className="size-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4" />
                    </div>
                    <div
                      className={cn(
                        "rounded-2xl px-4 py-2 max-w-[80%]",
                        chat.author_id === userMyId
                          ? "bg-primary text-primary-foreground rounded-tr-none"
                          : "bg-muted rounded-tl-none"
                      )}
                    >
                      <p className="text-xs text-gray-400">
                        {chat?.author?.name}
                      </p>
                      {(() => {
                        const messageContent = chat.text || chat.message;
                        const isHtml = /<[a-z][\s\S]*>/i.test(messageContent);

                        return isHtml ? (
                          <div
                            className="text-sm"
                            dangerouslySetInnerHTML={{ __html: messageContent }}
                          />
                        ) : (
                          <p className="text-sm">{messageContent}</p>
                        );
                      })()}
                      {chat.file && (
                        <div className="mt-2 flex items-center gap-2">
                          {getFileIcon(chat.file)}
                          <a
                            href={chat.file}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs hover:underline flex items-center gap-1"
                          >
                            Baixar anexo
                            <Download className="h-3 w-3" />
                          </a>
                        </div>
                      )}
                      <p
                        className={cn(
                          "text-[10px] mt-1",
                          chat.author_id === userMyId
                            ? "text-primary-foreground/70"
                            : "text-muted-foreground"
                        )}
                      >
                        {formatDate(chat?.created_at)}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Nenhuma mensagem ainda. Comece uma conversa!
                </p>
              )}
            </div>

            <div className="border-t p-2 sm:p-4 flex-shrink-0">
              <form onSubmit={handleSendMessage} className="space-y-2 sm:space-y-3">
                {selectedFiles.length > 0 && (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="flex items-center gap-2 bg-primary/5 p-2 sm:p-3 rounded-lg border border-primary/20">
                        {file.type.startsWith("image/") ? (
                          <img
                            src={URL.createObjectURL(file)}
                            alt="Preview"
                            className="w-12 h-12 object-cover rounded-md shrink-0"
                          />
                        ) : (
                          <FileIcon className="h-10 w-10 text-primary shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-foreground block truncate">
                            {file.name}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {(file.size / 1024).toFixed(1)} KB
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 hover:bg-destructive/10 hover:text-destructive shrink-0"
                          onClick={() => removeFile(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2 items-center w-full justify-between">
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileChange}
                    className="hidden"
                    id="file-upload"
                    disabled={sendingMessage}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.mp4,.webm,.ogg,.avi,.mov,.quicktime"
                    multiple
                  />
                  <Input
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Digite sua mensagem..."
                    disabled={sendingMessage}
                    className="w-full min-w-[8rem] sm:min-w-[20rem] h-10"
                  />
                  <div className="flex gap-2 items-center">
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    onClick={() => setShowCamera(true)}
                    disabled={sendingMessage}
                    className="h-10 w-10 flex-shrink-0"
                    title="Tirar foto"
                  >
                    <Camera className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={sendingMessage}
                    className="h-10 w-10 flex-shrink-0"
                    title="Anexar arquivo"
                  >
                    <PaperclipIcon className="h-4 w-4" />
                  </Button>
                  <Button
                    type="submit"
                    size="icon"
                    variant="default"
                    disabled={
                      (!message.trim() && selectedFiles.length === 0) || sendingMessage
                    }
                    className="h-10 w-10 flex-shrink-0"
                    title="Enviar mensagem"
                  >
                    {sendingMessage ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                  </div>
                  
                </div>
              </form>
            </div>
          </div>
        </div>

      <CameraModal
        isOpen={showCamera}
        onClose={() => setShowCamera(false)}
        onCapture={handleCameraCapture}
      />
    </>
  );
}
